import { createListenerMiddleware } from '@reduxjs/toolkit';
import { userActions } from 'src/store/client/slices/userSlice';
import userThunk from 'src/store/client/thunks/userThunk';

import type { GetUserInfoResponse } from '@/store/server/common/mypage/mypage.dto';

// Create the listener middleware instance
export const analyticsListenerMiddleware = createListenerMiddleware();

const calculateAgeGroup = (birth: string | null | undefined): string | null => {
  if (!birth) return null;

  const birthYear = birth.includes('-')
    ? Number(birth.split('-')[0])
    : Number(birth.slice(0, 4));

  if (isNaN(birthYear)) return null;

  const age = new Date().getFullYear() - birthYear;
  const decade = Math.floor(age / 10) * 10;
  return `${decade}대`;
};

const formatLinkedAccounts = (
  linking: { google: 'Y' | 'N'; kakao: 'Y' | 'N'; lpoint: 'Y' | 'N' } | undefined,
): string => {
  if (!linking) return '';
  return Object.entries(linking)
    .filter(([, value]) => value === 'Y')
    .map(([key]) => key)
    .join(',');
};

// Listen for login action to track GA4 analytics events
analyticsListenerMiddleware.startListening({
  actionCreator: userActions.login,
  effect: async (action) => {
    try {
      const { parseJwt } = await import('src/utils/auth');
      const { USER_TYPE_TO_AUTH_METHOD } = await import('@/libs/analytics/constants');
      const { trackEvent, setUserProperties } = await import('@/libs/analytics/events');

      const tokenInformation = parseJwt(action.payload.access_token);

      if (tokenInformation) {
        const method =
          USER_TYPE_TO_AUTH_METHOD[
            tokenInformation.tokenUserLoginType as keyof typeof USER_TYPE_TO_AUTH_METHOD
          ];

        if (method) {
          trackEvent({ name: 'login', params: { method } });
        }

        const userPermissionLevel = tokenInformation.tokenUserLevel;
        const userType = (() => {
          switch (userPermissionLevel) {
            case 'K101':
            case 'K102':
            case 'K103':
            case 'F103':
              return 'normal' as const;
            case 'K201':
            case 'F201':
              return 'verified' as const;
            default:
              return undefined;
          }
        })();

        setUserProperties({
          ...(method && { login_method: method }),
          ...(userType && { user_type: userType }),
          ...(userPermissionLevel && { user_level: userPermissionLevel }),
        });
      }
    } catch (error) {
      console.warn('Failed to track login analytics:', error);
    }
  },
});

// Listen for myDataLoad to set profile-based user properties
analyticsListenerMiddleware.startListening({
  actionCreator: userThunk.myDataLoad.fulfilled,
  effect: async (action) => {
    try {
      const { setUserProperties, setUserId } = await import('@/libs/analytics/events');

      const userInfo = action.payload as unknown as GetUserInfoResponse;

      // GA4 User-ID: 회원 고유키(비PII). 백엔드 회원 조인 키. mbr_id(이메일/전화=PII)는 사용 금지.
      if (userInfo.ma_mbr_key) {
        setUserId(userInfo.ma_mbr_key);
      }

      setUserProperties({
        gender: userInfo.gender ?? null,
        age_group: calculateAgeGroup(userInfo.birth),
        agree_marketing: userInfo.agree_marketing,
        linked_accounts: formatLinkedAccounts(userInfo.account_linking),
      });
    } catch (error) {
      console.warn('Failed to set profile user properties:', error);
    }
  },
});

// Listen for logout to clear GA4 User-ID
analyticsListenerMiddleware.startListening({
  actionCreator: userActions.logout,
  effect: async () => {
    try {
      const { setUserId } = await import('@/libs/analytics/events');
      setUserId(null);
    } catch (error) {
      console.warn('Failed to clear GA4 user_id:', error);
    }
  },
});
